<template>
  <transition name="parking-order-fade">
    <section
      v-if="visible"
      class="parking-order-layer"
      role="dialog"
      aria-modal="true"
      aria-labelledby="parking-order-title"
      @click.self="handleClose"
    >
      <div class="parking-order-panel">
        <header class="parking-order-head">
          <div class="parking-order-head__title">
            <span class="parking-order-head__icon" aria-hidden="true">
              <i class="el-icon-tickets" />
            </span>
            <div>
              <h2 id="parking-order-title">车辆详情</h2>
              <p>停车场进出场统计与收入明细</p>
            </div>
          </div>

          <div class="parking-order-head__actions">
            <button
              type="button"
              class="parking-order-close"
              aria-label="关闭"
              @click="handleClose"
            >
              <i class="el-icon-close" aria-hidden="true" />
            </button>
          </div>
        </header>

        <div class="parking-order-body">
          <div class="parking-chart-section">
            <div ref="vehicleChart" class="parking-chart" />
          </div>
          <div class="parking-order-table">
            <div v-if="!filteredLots.length" class="parking-order-empty">当前业态暂无停车数据</div>
            <template v-else>
            <div class="parking-order-table__head">
              <span class="col-lot">停车场</span>
              <span class="col-money col-money--total">总收入</span>
              <span class="col-count">总车位</span>
              <span class="col-money">临停收入</span>
              <span class="col-count">月租车位</span>
              <span class="col-money">月租车续费</span>
              <span class="col-money">储值车收入</span>
              <span class="col-money">内部车收入</span>
              <span class="col-action">操作</span>
            </div>
            <div
              v-for="(row, idx) in filteredLots"
              :key="idx"
              class="parking-order-table__row"
            >
              <button class="col-lot col-lot--link" :disabled="!canOpenOrder(row)" @click="openOrderWindow(row)">{{ row.name }}</button>
              <div class="col-money col-money--total">{{ row.total }}</div>
              <div class="col-count">{{ row.totalSpaces }}</div>
              <button class="col-money col-money--link" :disabled="!canOpenOrder(row)" @click="openOrderWindow(row, 'temp')">{{ row.temp }}</button>
              <div class="col-count">{{ row.monthSpaces }}</div>
              <button class="col-money col-money--link" :disabled="!canOpenOrder(row)" @click="openOrderWindow(row, 'month')">{{ row.month }}</button>
              <div class="col-money">{{ row.store }}</div>
              <div class="col-money">{{ row.inner }}</div>
              <div class="col-action">
                <button
                  type="button"
                  class="parking-order-view-btn"
                  :disabled="!canOpenOrder(row)"
                  @click="openOrderWindow(row)"
                >查看</button>
              </div>
            </div>
            </template>
          </div>
        </div>
      </div>
      <div
        v-if="pendingParkingLots.length > 1"
        class="parking-lot-picker"
        @click.self="cancelParkingSelection"
        @keydown.esc.stop.prevent="cancelParkingSelection"
      >
        <section class="parking-lot-picker__panel" role="dialog" aria-modal="true" aria-labelledby="parking-lot-picker-title">
          <div class="parking-lot-picker__head">
            <h3 id="parking-lot-picker-title">选择停车场</h3>
            <button type="button" class="parking-order-close" aria-label="取消选择" @click="cancelParkingSelection">
              <i class="el-icon-close" aria-hidden="true" />
            </button>
          </div>
          <p>请选择要查看的停车场</p>
          <div class="parking-lot-picker__options">
            <button
              v-for="lot in pendingParkingLots"
              :key="lot.parkingLotId"
              ref="parkingLotChoice"
              type="button"
              class="parking-lot-picker__option"
              @click="selectParkingLot(lot)"
            >
              <i class="el-icon-location-outline" aria-hidden="true" />
              <span>{{ lot.parkingLotName }}</span>
              <i class="el-icon-arrow-right" aria-hidden="true" />
            </button>
          </div>
        </section>
      </div>
    </section>
  </transition>
</template>

<script>
import Cookies from 'js-cookie'
import { buildParkingOrderSsoUrl } from '../utils/parkingOrderSso'

// 收入类型 → ai2park redirect 路径
const REVENUE_REDIRECT_MAP = {
  temp: 'park-data_report_temporary-charge',
  month: 'park-finance_report_vehicle-payment',
  operation: 'park-operation_record_parking'
}

// 二次刷新 dedup：整个会话只刷新一次（全局，不再按 parkingLotId 细分）
const REFRESH_FLAG_KEY = 'groupParkingOrder:refreshed'
const FALLBACK_WINDOW_FLAG = '__groupParkingOrderRefreshed__'

function resolveParkingLots (row) {
  if (!row) return []
  // 营收行可能汇总多个停车场；优先使用接口中的具名列表，兼容旧版单 ID 数据。
  const list = Array.isArray(row.parkingLotList) ? row.parkingLotList : [row]
  const seen = new Set()
  return list.reduce((lots, item) => {
    const id = item && item.parkingLotId != null ? String(item.parkingLotId).trim() : ''
    if (id.length < 5 || id.includes(',') || seen.has(id)) return lots
    seen.add(id)
    lots.push({
      parkingLotId: id,
      parkingLotName: String(item.parkingLotName || row.name || row.parkingLotName || '停车场').trim()
    })
    return lots
  }, [])
}

function isRefreshDone () {
  try {
    if (window && window[FALLBACK_WINDOW_FLAG]) return true
    return !!sessionStorage.getItem(REFRESH_FLAG_KEY)
  } catch (e) {
    return !!(window && window[FALLBACK_WINDOW_FLAG])
  }
}

function markRefreshDone () {
  try {
    sessionStorage.setItem(REFRESH_FLAG_KEY, '1')
  } catch (e) {
    // sessionStorage 不可用时用 window 变量兜底
  }
  if (window) {
    window[FALLBACK_WINDOW_FLAG] = true
  }
}

export default {
  name: 'ParkingOrderDialog',
  props: {
    visible: { type: Boolean, default: false },
    parkingLots: { type: Array, default: () => [] },
    vehicleFlowList: { type: Array, default: () => [] },
    activeTime: { type: String, default: '本月' },
    activeScope: { type: String, default: '全部' }
  },
  data () {
    return {
      pendingParkingLots: [],
      pendingRevenueType: ''
    }
  },
  computed: {
    vehicleFlowData () {
      // 业态、时间由主接口筛选；保留全部返回项，总车流量统一按进场 + 出场计算。
      return this.vehicleFlowList.map(item => {
        const enter = Number(item.enterCount)
        const exit = Number(item.exitCount)
        const enterCount = Number.isFinite(enter) ? enter : 0
        const exitCount = Number.isFinite(exit) ? exit : 0
        return { ...item, enterCount, exitCount, totalCount: enterCount + exitCount }
      })
    },
    filteredLots () {
      return this.parkingLots.filter(r => !r.empty)
    }
  },
  watch: {
    visible () {
      this.cancelParkingSelection()
      this.scheduleVehicleChart()
    },
    activeScope () {
      this.cancelParkingSelection()
    },
    activeTime () {
      this.cancelParkingSelection()
    },
    vehicleFlowList: {
      deep: true,
      handler () {
        this.scheduleVehicleChart()
      }
    },
    parkingLots: {
      deep: true,
      handler () {
        this.cancelParkingSelection()
      }
    }
  },
  mounted () {
    window.addEventListener('resize', this.onResize)
    this.scheduleVehicleChart()
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onResize)
    clearTimeout(this._vehicleChartTimer)
    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer)
      this._refreshTimer = null
    }
    if (this._vehicleChart) {
      this._vehicleChart.dispose()
      this._vehicleChart = null
    }
  },
  methods: {
    scheduleVehicleChart () {
      clearTimeout(this._vehicleChartTimer)
      if (!this.visible) return
      this.$nextTick(() => {
        if (!this.visible) return
        clearTimeout(this._vehicleChartTimer)
        // 等弹窗过渡结束后绘图，不再额外请求进出场接口。
        this._vehicleChartTimer = setTimeout(() => {
          this._vehicleChartTimer = null
          if (this.visible) this.initVehicleChart()
        }, 300)
      })
    },
    initVehicleChart () {
      const el = this.$refs.vehicleChart
      if (!el) return
      if (this._vehicleChart) {
        this._vehicleChart.dispose()
        this._vehicleChart = null
      }
      const chart = this.$echarts.init(el)
      this._vehicleChart = chart

      if (!this.vehicleFlowData.length) {
        chart.setOption({
          title: {
            text: '暂无进出场数据',
            left: 'center',
            top: 'center',
            textStyle: { color: 'rgba(186, 207, 232, 0.6)', fontSize: 14, fontWeight: 400 }
          }
        })
        return
      }

      const names = this.vehicleFlowData.map(d => d.projectName || d.parkingLotName || '--')
      chart.setOption({
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(6, 20, 42, 0.92)',
          borderColor: 'rgba(83, 181, 226, 0.52)',
          textStyle: { color: '#e4f0f8', fontSize: 12 }
        },
        legend: {
          bottom: 0,
          textStyle: { color: 'rgba(186, 207, 232, 0.88)', fontSize: 12 },
          data: ['车场总量', '进场', '退场']
        },
        grid: { top: 12, right: 24, bottom: 36, left: 12, containLabel: true },
        xAxis: {
          type: 'category',
          data: names,
          axisLine: { lineStyle: { color: 'rgba(109, 193, 192, 0.28)' } },
          axisTick: { show: false },
          axisLabel: {
            color: 'rgba(186, 207, 232, 0.84)',
            fontSize: 11,
            interval: 0,
            rotate: names.length > 5 ? 25 : 0
          }
        },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { color: 'rgba(109, 193, 192, 0.12)' } },
          axisLabel: { color: 'rgba(186, 207, 232, 0.7)', fontSize: 11 }
        },
        series: [
          {
            name: '车场总量',
            type: 'bar',
            barMaxWidth: 18,
            itemStyle: { color: '#00d4aa' },
            data: this.vehicleFlowData.map(d => d.totalCount)
          },
          {
            name: '进场',
            type: 'bar',
            barMaxWidth: 18,
            itemStyle: { color: '#38bdf8' },
            data: this.vehicleFlowData.map(d => d.enterCount)
          },
          {
            name: '退场',
            type: 'bar',
            barMaxWidth: 18,
            itemStyle: { color: '#f59e0b' },
            data: this.vehicleFlowData.map(d => d.exitCount)
          }
        ]
      })
      chart.off('click')
      chart.on('click', (params) => {
        if (!params || params.dataIndex == null) return
        const lot = this.vehicleFlowData[params.dataIndex]
        if (!lot || !lot.parkingLotId) return
        this.openOrderWindow(lot, 'operation')
      })
    },
    onResize () {
      if (this._vehicleChart) {
        this._vehicleChart.resize()
      }
    },
    token () {
      return Cookies.get('Admin-Token') || this.$store.getters.token || ''
    },
    canOpenOrder (row) {
      return resolveParkingLots(row).length > 0
    },
    openOrderWindow (row, revenueType) {
      if (this._refreshTimer) {
        clearTimeout(this._refreshTimer)
        this._refreshTimer = null
      }
      this.cancelParkingSelection()
      const lots = resolveParkingLots(row)
      if (!lots.length) return
      if (lots.length > 1) {
        this.pendingParkingLots = lots
        this.pendingRevenueType = revenueType || ''
        this.$nextTick(() => {
          const choices = this.$refs.parkingLotChoice
          if (choices && choices[0]) choices[0].focus()
        })
        return
      }
      this.navigateToParkingLot(lots[0], revenueType)
    },
    cancelParkingSelection () {
      this.pendingParkingLots = []
      this.pendingRevenueType = ''
    },
    selectParkingLot (lot) {
      const selected = this.pendingParkingLots.find(item => item.parkingLotId === lot.parkingLotId)
      if (!selected) return
      const revenueType = this.pendingRevenueType
      this.cancelParkingSelection()
      // 同步在用户点击时打开窗口，避免浏览器拦截。
      this.navigateToParkingLot(selected, revenueType)
    },
    navigateToParkingLot (lot, revenueType) {
      const lotId = lot.parkingLotId
      const t = this.token()
      if (!t) {
        this.$message.warning('登录信息已失效，请重新登录后再查看停车订单')
        return
      }

      const redirect = revenueType ? REVENUE_REDIRECT_MAP[revenueType] : undefined
      const url = buildParkingOrderSsoUrl({ token: t, parkId: lotId, redirect })
      if (!url) return

      const orderWindow = window.open(url, 'group-parking-orders')
      if (!orderWindow) {
        this.$message.warning('浏览器已拦截停车订单窗口，请允许弹出窗口后重试')
        return
      }
      orderWindow.focus()
      this.scheduleRefresh({ token: t, parkId: lotId, redirect })
    },
    scheduleRefresh ({ token, parkId, redirect }) {
      // 整个会话只刷新一次，避免不必要的二次刷新干扰
      if (isRefreshDone()) return
      markRefreshDone()

      // 延迟后重新导航同一窗口，解决 SSO 后停留在登录页的问题
      this._refreshTimer = setTimeout(() => {
        this._refreshTimer = null
        const url = buildParkingOrderSsoUrl({ token, parkId, redirect })
        if (!url) return
        window.open(url, 'group-parking-orders')
      }, 2000)
    },
    handleClose () {
      this.cancelParkingSelection()
      if (this._refreshTimer) {
        clearTimeout(this._refreshTimer)
        this._refreshTimer = null
      }
      this.$emit('close')
    }
  }
}
</script>

<style scoped lang="scss">
@import '../styles/design-system.scss';

// 全屏遮罩 — 与 EnergyDrilldownPanel 完全一致
.parking-order-layer {
  position: absolute !important;
  z-index: 80 !important;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 120px 170px 80px;
  background: rgba(2, 11, 27, 0.68);
  backdrop-filter: blur(2px) saturate(0.9);
  -webkit-backdrop-filter: blur(2px) saturate(0.9);
}

.parking-order-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 1160px;
  height: 680px;
  min-width: 0;
  min-height: 0;
  padding: 20px 22px;
  border: 1px solid rgba(83, 181, 226, 0.82);
  border-radius: 12px;
  background:
    radial-gradient(circle at 50% 0%, rgba(39, 126, 182, 0.2), transparent 44%),
    linear-gradient(180deg, #0b2a4a 0%, #071b35 100%);
  box-shadow:
    0 26px 76px rgba(0, 5, 18, 0.64),
    0 0 0 5px rgba(19, 95, 148, 0.22),
    inset 0 1px 0 rgba(205, 239, 255, 0.12);
  overflow: hidden;
  box-sizing: border-box;
}

.parking-lot-picker {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(2, 11, 27, 0.72);
  backdrop-filter: blur(3px);

  &__panel {
    width: 420px;
    max-width: calc(100% - 48px);
    padding: 22px;
    box-sizing: border-box;
    border: 1px solid rgba(83, 181, 226, 0.72);
    border-radius: 12px;
    background: linear-gradient(160deg, #103454, #081d36);
    box-shadow: 0 20px 60px rgba(0, 5, 18, 0.6);
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;

    h3 {
      margin: 0;
      color: #f4fbff;
      font-size: 18px;
    }
  }

  p {
    margin: 10px 0 18px;
    color: rgba(211, 231, 244, 0.76);
    font-size: 13px;
  }

  &__options {
    display: grid;
    gap: 12px;
    max-height: 320px;
    overflow-y: auto;
  }

  &__option {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 16px 14px;
    border: 1px solid rgba(83, 181, 226, 0.3);
    border-radius: 8px;
    background: rgba(19, 63, 94, 0.45);
    color: #8ee5f2;
    font: inherit;
    font-size: 14px;
    text-align: left;
    cursor: pointer;

    span {
      flex: 1;
      min-width: 0;
      color: #e4f0f8;
      overflow-wrap: anywhere;
    }

    &:hover,
    &:focus-visible {
      outline: 2px solid #8ee5f2;
      outline-offset: -2px;
      background: #164766;
    }
  }
}

.parking-order-head {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  min-height: 58px;
  padding: 0 0 16px;
  border-bottom: 1px solid rgba(101, 190, 229, 0.34);

  &__title {
    display: flex;
    align-items: center;
    gap: 13px;
    min-width: 0;
  }

  &__icon {
    display: grid;
    place-items: center;
    flex: 0 0 42px;
    width: 42px;
    height: 42px;
    border: 1px solid rgba(104, 202, 234, 0.58);
    border-radius: 9px;
    background: #0d385a;
    color: #8ee5f2;
    box-shadow: inset 0 0 18px rgba(66, 184, 222, 0.12);
    font-size: 23px;
  }

  h2 {
    margin: 0;
    color: #f4fbff;
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 0.3px;
  }

  p {
    margin: 4px 0 0;
    color: rgba(211, 231, 244, 0.76);
    font-size: 12px;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.parking-order-close {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid rgba(78, 157, 205, 0.46);
  border-radius: 7px;
  background: #071f3a;
  color: rgba(235, 244, 250, 0.88);
  font-size: 17px;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border-color: rgba(111, 210, 239, 0.78);
    background: #123d5f;
    color: #fff;
    outline: none;
  }
}

.parking-order-body {
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
}

// 车辆进出场柱状图
.parking-chart-section {
  position: absolute;
  top: 4px;
  left: 0;
  right: 0;
  height: 260px;
  overflow: hidden;
}

.parking-chart {
  width: 100%;
  height: 100%;
}

.parking-order-table {
  position: absolute;
  top: 322px;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: auto;
  border-top: 1px solid rgba(109, 193, 192, 0.18);
  padding: 14px 0 0;

  .parking-order-empty {
    display: grid;
    place-items: center;
    flex: 1;
    min-height: 260px;
    color: rgba(184, 213, 228, 0.72);
    font-size: 16px;
    letter-spacing: 0.5px;
  }

  &__head,
  &__row {
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) repeat(7, minmax(0, 0.82fr)) 52px;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  &__head {
    padding: 0 6px 10px;
    border-bottom: 1px solid rgba(109, 193, 192, 0.1);
    flex-shrink: 0;

    span {
      font-size: 13px;
      font-weight: 600;
      color: rgba(186, 207, 232, 0.92);
    }

    .col-money--total {
      color: rgba(132, 222, 220, 0.92);
    }
  }

  &__row {
    padding: 12px 6px;
    border-radius: 10px;
    background: rgba(8, 32, 60, 0.24);
    border: 1px solid rgba(109, 193, 192, 0.06);
    flex-shrink: 0;

    .col-lot {
      font-size: 13px;
      font-weight: 600;
      color: rgba(220, 236, 252, 0.96);
    }

    .col-money,
    .col-count {
      font-size: 13px;
      font-weight: 700;
      color: rgba(236, 245, 255, 0.96);
    }

    .col-money--total {
      color: $ds-cyan-bright;
    }

    .col-money--link {
      border: none;
      background: transparent;
      padding: 0;
      cursor: pointer;
      text-decoration: underline;
      text-underline-offset: 2px;
      font-family: $ds-font-num;
      font-variant-numeric: tabular-nums;
      font-size: 13px;
      font-weight: 700;
      color: rgba(236, 245, 255, 0.96);

      &:hover:not(:disabled) {
        color: $ds-cyan-bright;
      }

      &.col-money--total {
        color: $ds-cyan-bright;

        &:hover {
          color: lighten($ds-cyan-bright, 10%);
        }
      }
    }
  }
}

.col-lot {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &--link {
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
    text-align: left;
    color: rgba(220, 236, 252, 0.96);
    font-family: inherit;
    font-size: inherit;
    font-weight: 600;

    &:hover:not(:disabled) {
      color: $ds-cyan-bright;
      text-decoration: underline;
    }
  }
}

.col-money {
  min-width: 0;
  font-family: $ds-font-num;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
}

.col-count {
  min-width: 0;
  font-family: $ds-font-num;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
}

.col-action {
  text-align: center;
}

.parking-order-view-btn {
  padding: 3px 10px;
  border: 1px solid rgba(78, 157, 205, 0.38);
  border-radius: 4px;
  background: rgba(7, 31, 58, 0.64);
  color: rgba(235, 244, 250, 0.88);
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: rgba(111, 210, 239, 0.68);
    background: #123d5f;
    color: #fff;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.col-lot--link:disabled,
.col-money--link:disabled {
  cursor: default;
  text-decoration: none;
}

// 过渡动画
.parking-order-fade-enter-active,
.parking-order-fade-leave-active {
  transition: opacity 0.24s ease;

  .parking-order-panel {
    transition: transform 0.24s ease, opacity 0.24s ease;
  }
}

.parking-order-fade-enter,
.parking-order-fade-leave-to {
  opacity: 0;

  .parking-order-panel {
    transform: scale(0.96) translateY(10px);
    opacity: 0;
  }
}
</style>
